import { useEffect, useState, React, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";

import Header from "./header";
import SummaryCard from "./summarycard";
import ItemList from "./itemlist";
import Filter from "./filter";

function Quote() {
  const backendURL =
    process.env.NODE_ENV === "production"
      ? "https://quote.floorkitchenresource.com"
      : "http://localhost:3001";
  const [fetchedData, setFetchedData] = useState([]);

  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [lineOptions, setLineOptions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [multiplier, setMultiplier] = useState(() => {
    return localStorage.getItem("quote_multiplier") || "1";
  });
  const [multiplierInput, setMultiplierInput] = useState(() => {
    return localStorage.getItem("quote_multiplierInput") || "1";
  });

  const [companyName, setCompanyName] = useState(() => {
    return localStorage.getItem("quote_companyName") || "";
  });
  const [note, setNote] = useState(() => {
    return localStorage.getItem("quote_note") || "";
  });

  const filterRef = useRef(null);
  const fileInputRef = useRef(null);

  const [visibleRows, setVisibleRows] = useState(() => {
    try {
      const saved = localStorage.getItem("visibleRows");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error("Failed to load visibleRows from localStorage:", e);
      return {};
    }
  });

  const handleClearVisibleRows = () => {
    setVisibleRows({});
    localStorage.removeItem("visibleRows");
  };

  const handleImportQuote = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const decoded = decodeURIComponent(escape(atob(e.target.result)));
        const parsed = JSON.parse(decoded);

        if (parsed.visibleRows) setVisibleRows(parsed.visibleRows);
        if (parsed.multiplier) {
          setMultiplier(parsed.multiplier);
          setMultiplierInput(parsed.multiplier); // Sync UI
        }

        if (parsed.companyName !== undefined) {
          setCompanyName(parsed.companyName);
          localStorage.setItem("quote_companyName", parsed.companyName);
        }
        if (parsed.note !== undefined) {
          setNote(parsed.note);
          localStorage.setItem("quote_note", parsed.note);
        }

        // Optional: toast or message here like "Quote imported successfully"
      } catch (err) {
        alert("Failed to import quote. Make sure the file is valid.");
        console.error(err);
      }
    };

    reader.readAsText(file);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${backendURL}/item-detail`);
        if (!response.ok) throw new Error("Failed to fetch data");
        const data = await response.json();

        // Add a unique ID to each item
        const dataWithIds = data.map((item, index) => ({
          ...item,
          id: `item-${index}`, // or use uuid if available
        }));

        setFetchedData(dataWithIds);
      } catch (error) {
        console.error("Error fetching item list:", error);
      }
    };

    fetchData();
  }, [backendURL]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [colorsRes, linesRes] = await Promise.all([
          fetch(`${backendURL}/colors`),
          fetch(`${backendURL}/lines`),
        ]);
        const colorData = await colorsRes.json();
        const lineData = await linesRes.json();
        setColorOptions(colorData);
        setLineOptions(lineData);
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };

    fetchFilters();
  }, [backendURL]);

  useEffect(() => {
    const saved = localStorage.getItem("visibleRows");
    if (saved && fetchedData.length > 0) {
      try {
        const parsed = JSON.parse(saved);

        // Ensure each saved item has updated details from fetchedData
        const hydrated = {};
        for (const id in parsed) {
          const match = fetchedData.find((item) => item.id === id);
          if (match) {
            hydrated[id] = { ...match, ...parsed[id] };
          }
        }

        setVisibleRows(hydrated);
      } catch (err) {
        console.error("Failed to parse or hydrate visibleRows:", err);
      }
    }
  }, [fetchedData]);

  useEffect(() => {
    localStorage.setItem("visibleRows", JSON.stringify(visibleRows));
  }, [visibleRows]);

  return (
    <div>
      <Header />
      {/* Additional content for the Quote component can go here */}
      <input
        type="file"
        accept=".quote"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleImportQuote}
      />

      <Box sx={{ flexGrow: 1, maxWidth: "1920px", margin: "0 auto" }}>
        <Grid
          container
          spacing={2}
          style={{ padding: "40px 15px" }}
          direction={{ xs: "column", sm: "column", md: "column", lg: "row" }} // Adds responsive direction change
          wrap="nowrap"
        >
          <Grid item xs={12} sm={150} sx={{ flexShrink: 0 }} ref={filterRef}>
            <Filter
              selectedColors={selectedColors}
              setSelectedColors={setSelectedColors}
              selectedLines={selectedLines}
              setSelectedLines={setSelectedLines}
              colorOptions={colorOptions}
              lineOptions={lineOptions}
            />
          </Grid>
          <Grid item xs={12} sm sx={{ flexGrow: 1, minWidth: 300 }}>
            {/* Search Text Box */}
            <ItemList
              rows={fetchedData}
              setFetchedData={setFetchedData}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedColors={selectedColors}
              selectedLines={selectedLines}
              multiplier={multiplier}
              visibleRows={visibleRows}
              setVisibleRows={setVisibleRows}
              filterRef={filterRef}
              fileInputRef={fileInputRef}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={250}
            sx={{
              width: {
                xs: "100%", // Width 200px for extra-small to small screens
                md: "100%",
                lg: "300px",
              },
              flexShrink: 0,
            }}
          >
            <SummaryCard
              multiplier={multiplier}
              setMultiplier={setMultiplier}
              multiplierInput={multiplierInput}
              setMultiplierInput={setMultiplierInput}
              visibleRows={visibleRows}
              onClearVisibleRows={handleClearVisibleRows}
              companyName={companyName}
              setCompanyName={setCompanyName}
              note={note}
              setNote={setNote}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default Quote;
